import { useState } from "react";
import { actAs, actingAs, otherPeople, personFrom, rememberOther, TEST_PEOPLE, testBuild, testEmail } from "@/lib/testActAs";
import { testIds } from "@/testIds";

const T = testIds.testActAs;
const OWNER = { name: "Owner", email: "the test copy's default user" };

/**
 * "Sign in as…" on the test copy (the user, 11 Oct): a small pill in the
 * corner, "Test · Ana ▾", opening the people to switch between and a link to
 * the test inbox (Mailpit), where every email the test copy sends lands.
 * Rendered only in a test build (VITE_E2E); a production build drops it.
 * Under sheets and dialogs (z 39): it covered a phone sheet's labels (11 Oct).
 * Not under automation (23 Oct): no corner is free on every page (the rails' History and Trash
 * on the left, Ask Lilia's send on the right), and e2e acts as someone through localStorage
 * (e2e/helpers/testPerson), so the pill only ever blocked clicks there.
 */
export function TestActAsSwitcher() {
  const [open, setOpen] = useState(false);
  const [other, setOther] = useState("");
  if (!testBuild() || navigator.webdriver) return null;
  const me = actingAs();
  const typed = personFrom(other);
  const people = [...TEST_PEOPLE, ...otherPeople()];
  const signInOther = () => {
    if (!typed) return;
    rememberOther(typed);
    actAs(typed);
  };
  const mail = import.meta.env.VITE_TEST_MAIL_URL as string | undefined;
  const row = "flex w-full flex-col items-start rounded-md px-2.5 py-1.5 text-left hover:bg-[var(--lilia-hover)]";
  // Clear of the editor's panel rail (52px): at left-3 it covered the rail's last icon, History,
  // so nobody on the test copy could click it (16 Oct).
  return (
    <div className="fixed bottom-3 left-3 md:left-[60px] z-[39] max-md:bottom-[calc(env(safe-area-inset-bottom)+76px)]" data-testid={T.root}>
      {open && (
        <div
          role="menu"
          aria-label="Sign in as"
          className="mb-2 w-[240px] rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-1.5 text-[13px] shadow-lg"
        >
          <p className="px-2.5 pb-1 pt-1 text-[11.5px] font-semibold text-[var(--lilia-text-muted)]">Sign in as… (test copy only)</p>
          <button type="button" role="menuitemradio" aria-checked={!me} className={row} data-testid={T.person} data-person="owner" onClick={() => actAs(null)}>
            <span className="font-medium text-[var(--lilia-text)]">{OWNER.name}{!me ? " ✓" : ""}</span>
            <span className="text-[11.5px] text-[var(--lilia-text-muted)]">{OWNER.email}</span>
          </button>
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitemradio"
              aria-checked={me?.id === p.id}
              className={row}
              data-testid={T.person}
              data-person={p.id}
              onClick={() => actAs(p)}
            >
              <span className="font-medium text-[var(--lilia-text)]">{p.name}{me?.id === p.id ? " ✓" : ""}</span>
              <span className="text-[11.5px] text-[var(--lilia-text-muted)]">{testEmail(p.id, p.email)}</span>
            </button>
          ))}
          <form
            className="mt-1 border-t border-[var(--lilia-border)] px-2.5 pb-1.5 pt-2"
            onSubmit={(e) => {
              e.preventDefault();
              signInOther();
            }}
          >
            <label className="mb-1 block text-[11.5px] font-semibold text-[var(--lilia-text-muted)]" htmlFor="test-act-as-other">
              Other…
            </label>
            <div className="flex gap-1.5">
              <input
                id="test-act-as-other"
                data-testid={T.otherInput}
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="Dan Brown, or dan@…"
                autoComplete="off"
                className="min-w-0 flex-1 rounded-md border border-[var(--lilia-border)] bg-[var(--lilia-bg)] px-2 py-1 text-[12.5px] text-[var(--lilia-text)]"
              />
              <button
                type="submit"
                data-testid={T.otherGo}
                disabled={!typed}
                className="rounded-md bg-[var(--lilia-primary)] px-2.5 text-[12px] font-semibold text-[var(--lilia-on-primary,#fff)] disabled:opacity-40"
              >
                Go
              </button>
            </div>
            {typed && <p className="mt-1 truncate text-[11px] text-[var(--lilia-text-muted)]">{typed.name} · {testEmail(typed.id, typed.email)}</p>}
          </form>
          {mail && (
            <a
              href={mail}
              target="_blank"
              rel="noreferrer"
              data-testid={T.mail}
              className="mt-1 block border-t border-[var(--lilia-border)] px-2.5 pb-1 pt-2 text-[var(--lilia-primary)] hover:underline"
            >
              Test inbox (every email sent here) ↗
            </a>
          )}
        </div>
      )}
      <button
        type="button"
        data-testid={T.toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded-full border border-dashed border-[var(--lilia-warning,#8a5a00)] bg-[var(--lilia-surface)] px-3 py-1 text-[12px] font-semibold text-[var(--lilia-warning,#8a5a00)] shadow"
      >
        Test · {me ? me.name.split(" ")[0] : "Owner"} ▾
      </button>
    </div>
  );
}
